<template>
  <v-menu
    transition="slide-y-transition"
    location="bottom"
    :close-on-content-click="false"
    min-width="300"
    max-width="325"
  >
    <template v-slot:activator="{ props: active }">
      <v-btn
        style="border: solid 1px;"
        flat
        :color="onDarkBackground ? 'primary' : 'secondary'"
        dark
        v-bind="active"
        >{{ t('auth.login.btn') }}</v-btn
      >
    </template>
    <v-card>
      <v-form v-model="valid" ref="loginForm">
        <v-container>
          <v-row align="center">
            <v-col cols="12" md="12">
              <v-text-field
                v-model="username"
                :rules="nameRules"
                :counter="32"
                :label="t('auth.username.label')"
                required
                variant="underlined"
                @keydown="checkKeypress"
              />
            </v-col>
            <v-col cols="12" md="12">
              <v-text-field
                v-model="password"
                :rules="passwordRules"
                :label="t('auth.password.label')"
                required
                variant="underlined"
                type="password"
                @keydown="checkKeypress"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :class="onDarkBackground ? 'bg-primary' : 'bg-secondary'"
          :disabled="!valid"
          @click="initLogin"
          >{{ t('auth.login.btn') }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { eventBus } from '@/plugins/eventBus'
import snakeCaseToCamelCaseKey from '@/utils/SnakecaseToCamelcase'
import { AuthObject, User } from '@/types'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/store/modules/auth'

const authStore = useAuthStore()

const { t } = useI18n()

const username = ref<string>('')
const password = ref<string>('')
const valid = ref<boolean>(false)
const loginForm = ref<HTMLDivElement | null>(null)

const nameRules = [
  (v: string) => !!v || t('auth.username.rules.required'),
  (v: string) => v.length <= 32 || t('auth.username.rules.lessThan')
]

const passwordRules = [(v: string) => !!v || t('auth.password.rules.required')]

defineProps<{ onDarkBackground: boolean }>()

function initLogin() {
  const userToAuth: AuthObject = { username: username.value, password: password.value }

  authStore.authUser(userToAuth).then(
    (user: User) => {},
    (error: any) => {
      const { data } = error.response
      if (data.error != null) {
        const errorKey = snakeCaseToCamelCaseKey(data.error)
        eventBus.$emit('showError', t(`error.${errorKey}`))
      } else {
        eventBus.$emit('showError', t('auth.login.error'))
      }
    }
  )
}

function checkKeypress(keyboardEvent: KeyboardEvent) {
  if (keyboardEvent.key === 'Enter') {
    if (valid.value) {
      initLogin()
    } else {
      const { loginForm }: any = loginForm
      loginForm.validate()
    }
  }
}
</script>
